import { Ellipsis } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import type { IMenuOption } from "@/ui-kit";
import { IconButton, Menu } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";
import { notificationsCenter, useNotifications } from "../../model";

type IProps = {
  /** Opens the confirmation; owned by the trigger so it outlives the popover. */
  onDeleteAll: () => void;
};

enum MenuAction {
  MarkAllAsRead = "markAllAsRead",
  DeleteAll = "deleteAll",
}

export const HeaderMenu = ({ onDeleteAll }: IProps) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const hasItems = useNotifications((s) => s.items.length > 0);
  const hasUnread = useNotifications((s) => s.unreadCount > 0);

  const menuOptions = useMemo((): IMenuOption[] => {
    return [
      {
        id: MenuAction.MarkAllAsRead,
        text: t("action.markAllAsRead"),
        disabled: !hasUnread,
      },
      {
        id: MenuAction.DeleteAll,
        text: t("action.deleteAll"),
      },
    ];
  }, [t, hasUnread]);

  const onMenuChange = useCallback(
    (id: string) => {
      if (id === MenuAction.MarkAllAsRead) {
        notificationsCenter.markAllAsRead().catch(serverErrorHandler());
      }
      if (id === MenuAction.DeleteAll) {
        onDeleteAll();
      }
    },
    [onDeleteAll],
  );

  if (!hasItems) {
    return null;
  }

  return (
    <Menu options={menuOptions} onSelect={onMenuChange} placement="bottom-end">
      <IconButton
        size={isMobile ? "md" : "sm"}
        variant="ghost"
        color="neutral"
        content={<Ellipsis />}
        aria-label={t("label.actions")}
      />
    </Menu>
  );
};
